import React, { useState } from 'react';
import { X, Mail, User, Phone, Loader2, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CustomMuiTelInput } from './CustomMuiTelInput';
import { matchIsValidTel } from 'mui-tel-input'

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'login' | 'register';
}

export default function AuthModal({ isOpen, onClose, type }: AuthModalProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [telTouched, setTelTouched] = useState(false);

  const [formData, setFormData] = useState({
    prenom: '',
    capital: '',
    email: '',
    telephone: '',
    newsLetter: false, // Nouveau champ pour la newsletter
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const requestBody = {
      boardId: 7669473566,
      prenom: formData.prenom,
      capital: formData.capital,
      email: formData.email,
      telephone: formData.telephone,
      statut: 'Lead',
      newsLetter: true,
    };

    try {
      const response = await fetch('/api/user/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        // Sauvegarder l'email dans le localStorage pour l'utiliser ultérieurement
        localStorage.setItem('userEmail', formData.email);
        onClose();
        navigate('/trading-account');
      }
    } catch (error) {
      console.error('Erreur lors de la requête :', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh]">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg blur opacity-75" />
        <div className="relative bg-black rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-white transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="p-8">
            <h2 className="text-2xl font-bold text-white mb-2">Accès Discord à la formation</h2>
            <p className="text-gray-400 mb-6">
              Utilise tes coordonnées pour recevoir tes accès gratuitement 🔐
            </p>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Prénom */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Prénom
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>
                {/* Champ Téléphone réintégré */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Numéro de téléphone</label>
                  <CustomMuiTelInput
                    colorMode="dark"
                    value={formData.telephone}
                    onChange={(value: any) => setFormData({ ...formData, telephone: value })}
                    onBlur={() => setTelTouched(true)}
                    defaultCountry="FR"
                    disabled={isLoading}
                    fullWidth
                    error={telTouched && !matchIsValidTel(formData.telephone)}
                    helperText={
                      telTouched && !matchIsValidTel(formData.telephone)
                        ? 'Numéro de téléphone invalide'
                        : ''
                    }
                  />
              </div>
              {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Capital actuel prévu pour le trading
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />

                    <input
                      type="text"
                      name="nom"
                      value={formData.capital}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Budget en €"
                    />
                  </div>
                </div>
              </div>
              
              {/* Bouton d'inscription */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg py-3 px-4 font-medium hover:from-purple-700 hover:to-purple-900 transition-all transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Inscription en cours...
                  </>
                ) : (
                  "S'inscrire"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
